"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useStore,
  useUpdateStore,
  useCreateCategory,
  useCategoryPresets,
  useStoreCategories,
  STORE_TYPE_LABELS,
  type OrderWorkflowStage,
  type StoreType,
} from "@/api/store"
import { useToast } from "@/hooks/use-toast"
import {
  ArrowUp,
  ArrowDown,
  GripVertical,
  Plus,
  Trash2,
  Store,
  Workflow,
  Sparkles,
} from "lucide-react"

const STORE_TYPES: { value: StoreType; label: string; desc: string }[] = [
  { value: "restaurant", label: "Restaurant", desc: "Food & beverage ordering" },
  { value: "food_kitchen", label: "Food / Kitchen", desc: "Prepared meals & snacks" },
  { value: "supplements", label: "Supplements & Protein", desc: "Health & fitness supplements" },
  { value: "clothing", label: "Clothing", desc: "Apparel & activewear" },
  { value: "merchandise", label: "Merchandise", desc: "Branded gym merchandise" },
  { value: "retail", label: "Retail", desc: "General physical products" },
  { value: "services", label: "Services", desc: "Service-based offerings" },
  { value: "general", label: "General Store", desc: "Multi-purpose store" },
  { value: "other", label: "Other", desc: "Custom store type" },
]

const PRESET_COLORS = [
  "#f59e0b",
  "#3b82f6",
  "#f97316",
  "#22c55e",
  "#8b5cf6",
  "#ec4899",
  "#6b7280",
  "#ef4444",
]

type WorkflowDraftStage = {
  key: string
  label: string
  color: string
  order: number
  isTerminal: boolean
  isCancellation: boolean
  allowedNext: string[]
}

function normalizeKey(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
}

export function StoreSettingsTab() {
  const { toast } = useToast()
  const { data: store, isLoading } = useStore()
  const updateStore = useUpdateStore()
  const createCategoryMut = useCreateCategory()
  const { data: categories } = useStoreCategories()
  const [creatingPresets, setCreatingPresets] = useState(false)

  const [name, setName] = useState("Store")
  const [description, setDescription] = useState("")
  const [enabled, setEnabled] = useState(true)
  const [currency, setCurrency] = useState("ALL")
  const [storeType, setStoreType] = useState<StoreType>("restaurant")
  const [entryStatusKey, setEntryStatusKey] = useState("")
  const [orderWorkflow, setOrderWorkflow] = useState<WorkflowDraftStage[]>([])

  const { data: presetsData } = useCategoryPresets(storeType)
  const presetNames = presetsData?.presets ?? []
  const existingCategoryNames = new Set((categories ?? []).map(c => c.name.toLowerCase()))
  const hasNewPresets = presetNames.some(p => !existingCategoryNames.has(p.toLowerCase()))

  const handleCreatePresetCategories = async () => {
    if (!presetNames.length) return
    setCreatingPresets(true)
    try {
      let order = (categories ?? []).length
      for (const presetName of presetNames) {
        if (existingCategoryNames.has(presetName.toLowerCase())) continue
        await createCategoryMut.mutateAsync({ name: presetName, displayOrder: order++ })
      }
      toast({ title: "Preset categories created" })
    } catch {
      toast({ title: "Failed to create some categories", variant: "destructive" })
    } finally {
      setCreatingPresets(false)
    }
  }

  useEffect(() => {
    if (store) {
      setName(store.name)
      setDescription(store.description ?? "")
      setEnabled(store.enabled)
      setCurrency(store.currency ?? "ALL")
      setStoreType(store.storeType ?? "restaurant")

      const stages = (store.orderWorkflow ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((stage) => ({
          key: stage.key,
          label: stage.label,
          color: stage.color ?? "",
          order: stage.order,
          isTerminal: !!stage.isTerminal,
          isCancellation: !!stage.isCancellation,
          allowedNext: stage.allowedNext ?? [],
        }))
      setOrderWorkflow(stages)
      const defaultEntry = store.entryStatusKey ?? stages.find((s) => !s.isTerminal)?.key ?? ""
      setEntryStatusKey(defaultEntry)
    }
  }, [store])

  const moveStage = (index: number, direction: -1 | 1) => {
    setOrderWorkflow((prev) => {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= prev.length) return prev
      const next = [...prev]
      const temp = next[index]
      next[index] = next[targetIndex]
      next[targetIndex] = temp
      return next.map((stage, i) => ({ ...stage, order: i }))
    })
  }

  const addStage = () => {
    setOrderWorkflow((prev) => [
      ...prev,
      {
        key: "",
        label: "",
        color: PRESET_COLORS[prev.length % PRESET_COLORS.length],
        order: prev.length,
        isTerminal: false,
        isCancellation: false,
        allowedNext: [],
      },
    ])
  }

  const removeStage = (index: number) => {
    const removedKey = normalizeKey(orderWorkflow[index].key)
    setOrderWorkflow((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((stage, i) => ({
          ...stage,
          order: i,
          allowedNext: stage.allowedNext.filter((k) => k !== removedKey),
        })),
    )
  }

  const updateStage = (index: number, patch: Partial<WorkflowDraftStage>) => {
    setOrderWorkflow((prev) =>
      prev.map((stage, i) => {
        if (i !== index) return stage
        const updated = { ...stage, ...patch }
        if (patch.label !== undefined && (!stage.key || stage.key === normalizeKey(stage.label))) {
          updated.key = normalizeKey(patch.label)
        }
        if (patch.isCancellation) {
          updated.isTerminal = true
          updated.allowedNext = []
        }
        if (patch.isTerminal) {
          updated.allowedNext = []
        }
        return updated
      }),
    )
  }

  const toggleAllowedNext = (stageIndex: number, targetKey: string) => {
    setOrderWorkflow((prev) =>
      prev.map((stage, i) => {
        if (i !== stageIndex) return stage
        const has = stage.allowedNext.includes(targetKey)
        return {
          ...stage,
          allowedNext: has
            ? stage.allowedNext.filter((k) => k !== targetKey)
            : [...stage.allowedNext, targetKey],
        }
      }),
    )
  }

  const validateAndBuildWorkflow = (): { workflow: OrderWorkflowStage[]; entryKey: string } | null => {
    if (orderWorkflow.length === 0) {
      toast({ title: "Add at least one workflow stage", variant: "destructive" })
      return null
    }

    const normalized = orderWorkflow.map((stage, index) => {
      const key = normalizeKey(stage.key)
      const label = stage.label.trim()
      const isCancellation = !!stage.isCancellation || key === "cancelled"
      const isTerminal = !!stage.isTerminal || isCancellation
      return {
        key,
        label,
        color: stage.color.trim(),
        order: index,
        isTerminal,
        isCancellation,
        allowedNext: isTerminal ? [] : stage.allowedNext.map(normalizeKey).filter(Boolean),
      }
    })

    if (normalized.some((stage) => !stage.key || !stage.label)) {
      toast({ title: "Each stage needs a name", variant: "destructive" })
      return null
    }

    const uniqueKeyCount = new Set(normalized.map((stage) => stage.key)).size
    if (uniqueKeyCount !== normalized.length) {
      toast({ title: "Stage names must be unique", variant: "destructive" })
      return null
    }

    const nonTerminalStages = normalized.filter((stage) => !stage.isTerminal)
    if (nonTerminalStages.length === 0) {
      toast({ title: "At least one stage must be non-terminal", variant: "destructive" })
      return null
    }

    const validKeys = new Set(normalized.map((stage) => stage.key))
    const workflow: OrderWorkflowStage[] = normalized.map((stage) => ({
      ...stage,
      color: stage.color || undefined,
      allowedNext: stage.isTerminal
        ? []
        : Array.from(
            new Set(
              stage.allowedNext.filter(
                (key) => key !== stage.key && validKeys.has(key),
              ),
            ),
          ),
    }))

    const normalizedEntry = normalizeKey(entryStatusKey)
    const entryStage = workflow.find((stage) => stage.key === normalizedEntry && !stage.isTerminal)
    return { workflow, entryKey: entryStage?.key ?? nonTerminalStages[0].key }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const workflowConfig = validateAndBuildWorkflow()
    if (!workflowConfig) return

    try {
      await updateStore.mutateAsync({
        name,
        description: description || undefined,
        enabled,
        currency: currency || "ALL",
        storeType,
        orderWorkflow: workflowConfig.workflow,
        entryStatusKey: workflowConfig.entryKey,
      })
      toast({ title: "Store settings saved" })
    } catch {
      toast({ title: "Failed to save settings", variant: "destructive" })
    }
  }

  const isPending = updateStore.isPending

  if (isLoading) {
    return <p className="text-muted-foreground">Loading...</p>
  }

  const nonTerminalStages = orderWorkflow.filter((s) => !s.isTerminal)

  return (
    <form onSubmit={handleSave} className="space-y-6">
      {/* ── General Settings ── */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">General</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="store-name">Store Name *</Label>
              <Input
                id="store-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Store"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="store-type">Store Type</Label>
              <Select value={storeType} onValueChange={(v) => setStoreType(v as StoreType)}>
                <SelectTrigger id="store-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STORE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {hasNewPresets && presetNames.length > 0 && (
            <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 flex items-center justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="text-sm">
                  <span className="font-medium">Suggested categories for {STORE_TYPE_LABELS[storeType] ?? storeType}:</span>{" "}
                  <span className="text-muted-foreground">{presetNames.join(", ")}</span>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleCreatePresetCategories}
                disabled={creatingPresets}
                className="shrink-0"
              >
                {creatingPresets ? "Creating..." : "Add All"}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="store-currency">Currency</Label>
              <Input
                id="store-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                placeholder="ALL"
                maxLength={5}
              />
              <p className="text-xs text-muted-foreground">e.g. ALL, EUR, USD</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="store-desc">Description</Label>
              <Textarea
                id="store-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief store description..."
                rows={2}
              />
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <Label className="cursor-pointer font-medium">Store Enabled</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When disabled, members won't see the store.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </CardContent>
      </Card>

      {/* ── Order Workflow ── */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Workflow className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-base">Order Workflow</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Define the stages an order moves through. These appear as columns on the Orders board.
                </p>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addStage} className="shrink-0">
              <Plus className="h-4 w-4 mr-1" />
              Add Stage
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {orderWorkflow.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground rounded-lg border border-dashed">
              No stages yet. Add your first workflow stage above.
            </div>
          )}

          {orderWorkflow.map((stage, index) => {
            const stageKey = normalizeKey(stage.key)
            const otherStages = orderWorkflow
              .map((s, i) => ({ ...s, index: i }))
              .filter((s) => s.index !== index && normalizeKey(s.key))

            return (
              <div
                key={`stage-${index}`}
                className="rounded-lg border bg-card shadow-sm"
              >
                {/* Stage header row */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-muted/30 rounded-t-lg">
                  <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />

                  <div
                    className="h-4 w-4 rounded-full shrink-0 border"
                    style={{
                      backgroundColor: stage.color || "#6b7280",
                      borderColor: stage.color || "#6b7280",
                    }}
                  />

                  <span className="font-medium text-sm flex-1 truncate">
                    {stage.label || `Stage ${index + 1}`}
                  </span>

                  {stageKey && (
                    <code className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {stageKey}
                    </code>
                  )}

                  {stage.isTerminal && !stage.isCancellation && (
                    <Badge variant="secondary" className="text-[11px]">Terminal</Badge>
                  )}
                  {stage.isCancellation && (
                    <Badge variant="destructive" className="text-[11px]">Cancellation</Badge>
                  )}

                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveStage(index, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveStage(index, 1)}
                      disabled={index === orderWorkflow.length - 1}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeStage(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Stage body */}
                <div className="px-3 py-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Stage Name *</Label>
                      <Input
                        value={stage.label}
                        onChange={(e) => updateStage(index, { label: e.target.value })}
                        placeholder="e.g. Preparing"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Color</Label>
                      <div className="flex items-center gap-1.5">
                        {PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className="h-7 w-7 rounded-full border-2 transition-all hover:scale-110"
                            style={{
                              backgroundColor: color,
                              borderColor: stage.color === color ? "var(--foreground)" : "transparent",
                            }}
                            onClick={() => updateStage(index, { color })}
                          />
                        ))}
                        <Input
                          value={stage.color}
                          onChange={(e) => updateStage(index, { color: e.target.value })}
                          placeholder="#hex"
                          className="w-20 h-7 text-xs ml-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <Switch
                        checked={stage.isTerminal}
                        onCheckedChange={(checked) => updateStage(index, { isTerminal: checked })}
                        disabled={stage.isCancellation}
                      />
                      Terminal stage
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Switch
                        checked={stage.isCancellation}
                        onCheckedChange={(checked) => updateStage(index, { isCancellation: checked })}
                      />
                      Cancellation
                    </label>
                  </div>

                  {!stage.isTerminal && !stage.isCancellation && otherStages.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Can move to</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {otherStages.map((other) => {
                          const otherKey = normalizeKey(other.key)
                          if (!otherKey) return null
                          const isSelected = stage.allowedNext.includes(otherKey)
                          return (
                            <button
                              key={otherKey}
                              type="button"
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                                isSelected
                                  ? "border-primary bg-primary/10 text-primary font-medium"
                                  : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                              }`}
                              onClick={() => toggleAllowedNext(index, otherKey)}
                            >
                              <div
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: other.color || "#6b7280" }}
                              />
                              {other.label || otherKey}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {nonTerminalStages.length > 0 && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <Label>New Order Entry Stage</Label>
                <p className="text-xs text-muted-foreground">
                  When a new order is placed, it starts in this stage.
                </p>
                <Select value={entryStatusKey} onValueChange={setEntryStatusKey}>
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder="Select entry stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {nonTerminalStages.map((stage) => {
                      const key = normalizeKey(stage.key) || `stage-${stage.order}`
                      return (
                        <SelectItem key={key} value={key}>
                          {stage.label || key}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Button type="submit" disabled={isPending || !name.trim()} size="lg">
        {isPending ? "Saving..." : "Save Changes"}
      </Button>
    </form>
  )
}
